import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { Box, Typography, Link, Divider, Chip, Alert } from "@mui/material";
import {
  ArrowBack,
  Shield,
  Security,
  Storage,
  Share,
  ContactSupport,
  Update,
  KeyboardArrowUp,
} from "@mui/icons-material";

import logo from "../../assets/nwt.png";
import {
  PrivacyCard,
  PrivacyContainer,
  ScrollTopSlot,
  SectionCard,
  TableOfContents,
} from "./styles";
import { Section } from "./types";
import { Fab } from "../../components/Fab";

export const PrivacyPolicyPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>("");
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [, navigate] = useLocation();

  const lastUpdated = "May 1, 2025";
  const effectiveDate = "June 1, 2025";

  useEffect(() => {
    let frame = 0;

    const handleScroll = () => {
      if (frame) {
        return;
      }
      frame = globalThis.window.requestAnimationFrame(() => {
        setShowScrollTop(window.scrollY > 300);
        frame = 0;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame) {
        globalThis.window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  useEffect(() => {
    const sectionEls = document.querySelectorAll("[data-section]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }
          const id = (entry.target as HTMLElement).dataset.section;
          if (id) {
            setActiveSection(id);
          }
        });
      },
      { rootMargin: "-100px 0px -66% 0px" },
    );

    sectionEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const goBack = () => {
    if (globalThis.window.history.length > 1) {
      globalThis.window.history.back();
      return;
    }
    navigate("/");
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(`[data-section="${sectionId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const sections: Section[] = useMemo(
    () => [
      {
        id: "overview",
        title: "Privacy Overview",
        icon: <Shield sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              At Net Worth Tracker, your privacy is fundamental to our mission.
              We believe that financial management should be private, secure,
              and entirely under your control.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Key Principle:</strong> We operate on a privacy-first
                model. Your financial data is securely stored on our servers,
                never sold, and never shared with third parties without your
                explicit consent.
              </Typography>
            </Alert>
            <Typography variant="body1">
              This policy explains how we collect, use, and protect your
              information when you use our financial tracking application.
            </Typography>
          </Box>
        ),
      },
      {
        id: "collection",
        title: "Information We Collect",
        icon: <Storage sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "text.primary" }}
            >
              Personal Information
            </Typography>
            <Typography variant="body1">
              We collect information you provide directly to us:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body2">
                <strong>Account Information:</strong> Name, email address, and
                password for account creation
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Profile Data:</strong> Optional profile information you
                choose to provide
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Communication:</strong> Messages you send us for support
                or feedback
              </Typography>
            </Box>

            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "text.primary", mt: 3 }}
            >
              Financial Data
            </Typography>
            <Typography variant="body1">
              Your financial information is stored securely on our servers:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body2">
                <strong>Transaction Data:</strong> Income, expenses, and
                financial transactions you manually enter
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Account Balances:</strong> Savings, investment, and debt
                information you track
              </Typography>
            </Box>

            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> We do not connect to your bank
                accounts, credit cards, or any financial institutions. All
                financial data is manually entered by you and stored securely on
                our servers.
              </Typography>
            </Alert>
          </Box>
        ),
      },
      {
        id: "usage",
        title: "How We Use Your Information",
        icon: <Security sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              We use the information we collect for the following purposes:
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Service Provision
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body2">
                  Provide and maintain your Net Worth Tracker account
                </Typography>
                <Typography component="li" variant="body2">
                  Enable financial tracking and reporting features
                </Typography>
                <Typography component="li" variant="body2">
                  Sync your data across your devices
                </Typography>
              </Box>
            </SectionCard>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Communication
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body2">
                  Send you important updates about our service
                </Typography>
                <Typography component="li" variant="body2">
                  Respond to your support requests and inquiries
                </Typography>
                <Typography component="li" variant="body2">
                  Send optional newsletters (only with your consent)
                </Typography>
              </Box>
            </SectionCard>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Improvement & Analytics
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body2">
                  Should we introduce usage analytics in the future, we will
                  analyze patterns to improve our application using anonymized
                  data only, and update this policy accordingly
                </Typography>
                <Typography component="li" variant="body2">
                  Identify and fix technical issues
                </Typography>
                <Typography component="li" variant="body2">
                  Develop new features based on user needs
                </Typography>
              </Box>
            </SectionCard>
          </Box>
        ),
      },
      {
        id: "sharing",
        title: "Information Sharing",
        icon: <Share sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>
                  We do not sell, rent, or trade your personal information to
                  third parties for marketing purposes.
                </strong>
              </Typography>
            </Alert>

            <Typography variant="body1">
              We may share your information only in the following limited
              circumstances:
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Service Providers
              </Typography>
              <Typography variant="body2">
                We work with trusted third-party service providers who help us
                operate our service:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body2">
                  Cloud hosting providers (for application infrastructure and
                  secure storage)
                </Typography>
                <Typography component="li" variant="body2">
                  Email service providers (for account communications)
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                All service providers are contractually bound to protect your
                information and use it only for the specific services they
                provide to us.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                We do not currently use third-party analytics providers. Should
                we introduce usage analytics in the future, we will limit shared
                data to anonymized usage information and update this policy.
              </Typography>
            </SectionCard>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Legal Requirements
              </Typography>
              <Typography variant="body2">
                We may disclose your information if required by law, such as to
                comply with a subpoena or similar legal process, or when we
                believe disclosure is necessary to protect our rights, your
                safety, or the safety of others.
              </Typography>
            </SectionCard>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Business Transfers
              </Typography>
              <Typography variant="body2">
                In the event of a merger, acquisition, or sale of assets, your
                information may be transferred. We will notify you before your
                information becomes subject to a different privacy policy.
              </Typography>
            </SectionCard>
          </Box>
        ),
      },
      {
        id: "security",
        title: "Data Security",
        icon: <Security sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              We implement industry-standard security measures to protect your
              information:
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
                mb: 3,
              }}
            >
              <SectionCard>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "text.primary" }}
                >
                  Encryption
                </Typography>
                <Typography variant="body2">
                  All data transmission is protected with HTTPS encryption.
                  Sensitive credentials, such as authentication and verification
                  tokens, are additionally encrypted using industry-standard
                  protocols, and we strictly limit access to stored data.
                </Typography>
              </SectionCard>

              <SectionCard>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "text.primary" }}
                >
                  Access Controls
                </Typography>
                <Typography variant="body2">
                  Strict access controls ensure only authorized personnel can
                  access systems containing personal information, and only when
                  necessary.
                </Typography>
              </SectionCard>

              <SectionCard>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "text.primary" }}
                >
                  Cloud Storage
                </Typography>
                <Typography variant="body2">
                  Your financial data is stored securely on cloud servers,
                  allowing for easier access, backups, and protection against
                  local device risks.
                </Typography>
              </SectionCard>

              <SectionCard>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "text.primary" }}
                >
                  Regular Audits
                </Typography>
                <Typography variant="body2">
                  We regularly review and update our security practices and
                  conduct security assessments to identify and address potential
                  vulnerabilities.
                </Typography>
              </SectionCard>
            </Box>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Your Role in Security:</strong> You can enhance your
                account security by using a strong, unique password.
              </Typography>
            </Alert>
          </Box>
        ),
      },
      {
        id: "rights",
        title: "Your Rights & Choices",
        icon: <ContactSupport sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              You have several rights regarding your personal information:
            </Typography>

            <Box sx={{ display: "grid", gap: 2 }}>
              <SectionCard>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "text.primary" }}
                >
                  Access & Portability
                </Typography>
                <Typography variant="body2">
                  You can access, download, or export your account information
                  and financial data at any time through your account settings.
                </Typography>
                <Chip
                  label="Available in Settings"
                  size="small"
                  variant="outlined"
                />
              </SectionCard>

              <SectionCard>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "text.primary" }}
                >
                  Correction & Updates
                </Typography>
                <Typography variant="body2">
                  You can update your personal information, financial data, and
                  account preferences directly in the application.
                </Typography>
                <Chip label="Self-Service" size="small" variant="outlined" />
              </SectionCard>

              <SectionCard>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "text.primary" }}
                >
                  Deletion
                </Typography>
                <Typography variant="body2">
                  You can delete your account and all associated data at any
                  time. This action is irreversible and will permanently remove
                  all your information from our servers.
                </Typography>
                <Chip
                  label="Permanent Action"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </SectionCard>

              <SectionCard>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "text.primary" }}
                >
                  Communication Preferences
                </Typography>
                <Typography variant="body2">
                  You can opt out of non-essential communications, such as
                  newsletters and product updates, while still receiving
                  important account notifications.
                </Typography>
                <Chip label="Customizable" size="small" variant="outlined" />
              </SectionCard>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Need Help?</strong> If you need assistance exercising
                any of these rights, please contact our support team at{" "}
                <Link
                  href="mailto:my.ntw.app@gmail.com"
                  sx={{ color: "var(--color-primary)" }}
                >
                  my.ntw.app@gmail.com
                </Link>
              </Typography>
            </Alert>
          </Box>
        ),
      },
      {
        id: "updates",
        title: "Policy Updates",
        icon: <Update sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or legal requirements.
            </Typography>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Notification Process
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0 }}>
                <Typography component="li" variant="body2">
                  We will notify you of any material changes via email
                </Typography>
                <Typography component="li" variant="body2">
                  Updates will be posted on our website and in the application
                </Typography>
                <Typography component="li" variant="body2">
                  You will have 30 days to review changes before they take
                  effect
                </Typography>
              </Box>
            </SectionCard>

            <SectionCard>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Version History
              </Typography>
              <Typography variant="body2">
                <strong>Current Version:</strong> 2.0 (Effective:{" "}
                {effectiveDate})
              </Typography>
              <Typography variant="body2">
                <strong>Last Updated:</strong> {lastUpdated}
              </Typography>
              <Typography variant="body2">
                <strong>Previous Version:</strong> 1.0 (January 2025)
              </Typography>
            </SectionCard>
          </Box>
        ),
      },
      {
        id: "legal-basis",
        title: "Legal Basis for Processing",
        icon: <Security sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              If you are located in the European Economic Area (EEA), the United
              Kingdom (UK), or other regions with similar laws, we process your
              personal information under the following legal bases:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body2">
                <strong>Consent:</strong> When you create an account and choose
                to provide information, you consent to its use.
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Contractual Necessity:</strong> Processing is necessary
                to provide you with our services.
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Legal Obligations:</strong> To comply with applicable
                laws and regulations.
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Legitimate Interests:</strong> To improve, secure, and
                develop our services, provided these interests do not override
                your fundamental rights and freedoms.
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        id: "retention",
        title: "Data Retention",
        icon: <Update sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              We retain your information only for as long as necessary to
              provide our services or comply with legal obligations:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body2">
                Your data is stored for as long as your account remains active.
              </Typography>
              <Typography component="li" variant="body2">
                If your account remains inactive for more than 24 months, we may
                anonymize or delete your data.
              </Typography>
              <Typography component="li" variant="body2">
                You may delete your account at any time, which permanently
                erases your personal information from our servers.
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        id: "children",
        title: "Children’s Privacy",
        icon: <ContactSupport sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              Net Worth Tracker is not directed to children under 16, and we do
              not knowingly collect personal information from anyone under this
              age.
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              If we become aware that we have collected information from a child
              under 16, we will take steps to delete it promptly. If you believe
              a child has provided us with personal information, please contact
              us at{" "}
              <Link
                href="mailto:my.ntw.app@gmail.com"
                sx={{ color: "var(--color-primary)" }}
              >
                my.ntw.app@gmail.com
              </Link>
              .
            </Typography>
          </Box>
        ),
      },
      {
        id: "transfers",
        title: "International Data Transfers",
        icon: <Storage sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              Your information may be processed and stored on servers located
              outside your country of residence. These countries may have
              different data protection laws than your home country.
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Where we transfer personal data internationally, we implement
              safeguards such as Standard Contractual Clauses (SCCs) or
              equivalent protections to ensure your information remains
              protected.
            </Typography>
          </Box>
        ),
      },
      {
        id: "contact",
        title: "Contact Information",
        icon: <ContactSupport sx={{ color: "text.secondary" }} />,
        content: (
          <Box>
            <Typography variant="body1">
              If you have any questions or concerns about this Privacy Policy or
              our data practices, you can reach us at:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, mt: 2 }}>
              <Typography component="li" variant="body2">
                <strong>Email:</strong>{" "}
                <Link
                  href="mailto:my.ntw.app@gmail.com"
                  sx={{ color: "var(--color-primary)" }}
                >
                  my.ntw.app@gmail.com
                </Link>
              </Typography>
              {/* <Typography component="li" variant="body2">
                <strong>Mailing Address:</strong> Net Worth Tracker, Rua Exemplo 123, 3500-000 Viseu, Portugal
              </Typography> */}
            </Box>
          </Box>
        ),
      },
    ],
    [],
  );

  const tableOfContents = sections.map((section) => ({
    id: section.id,
    title: section.title,
    icon: section.icon,
  }));

  return (
    <PrivacyContainer>
      <PrivacyCard elevation={0}>
        {/* Header */}
        <Box component="header" sx={{ mb: 5 }}>
          <Link
            component="button"
            onClick={goBack}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mb: 4,
              color: "var(--color-primary)",
              fontWeight: "medium",
              textDecoration: "none",
            }}
          >
            <ArrowBack fontSize="small" />
            Back
          </Link>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Box
              component="img"
              src={logo}
              alt=""
              sx={{ height: 28, width: "auto", display: "block" }}
            />
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", letterSpacing: "0.08em" }}
            >
              Net Worth Tracker
            </Typography>
          </Box>

          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: "bold", color: "text.primary", mb: 1 }}
          >
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Effective {effectiveDate} · Updated {lastUpdated}
          </Typography>
        </Box>

        {/* Table of Contents */}
        <TableOfContents>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: "bold", color: "text.primary" }}
          >
            Table of Contents
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              gap: 1,
            }}
          >
            {tableOfContents.map((item, index) => (
              <Link
                key={item.id}
                component="button"
                variant="body2"
                onClick={() => scrollToSection(item.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                  gap: 1,
                  p: 1,
                  borderRadius: 1,
                  textAlign: "left",
                  border: "none",
                  background:
                    activeSection === item.id
                      ? "color-mix(in srgb, var(--color-primary) 8%, transparent)"
                      : "transparent",
                  color:
                    activeSection === item.id
                      ? "var(--color-primary)"
                      : "text.primary",
                  "&:hover": {
                    background:
                      "color-mix(in srgb, var(--color-primary) 5%, transparent)",
                  },
                }}
              >
                {item.icon}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: activeSection === item.id ? "medium" : "normal",
                  }}
                >
                  {index + 1}. {item.title}
                </Typography>
              </Link>
            ))}
          </Box>
        </TableOfContents>

        {/* Content Sections */}
        <Box>
          {sections.map((section, index) => (
            <Box
              key={section.id}
              id={section.id}
              data-section={section.id}
              sx={{ mb: 5, scrollMarginTop: "24px" }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                {section.icon}
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "text.primary" }}
                >
                  {index + 1}. {section.title}
                </Typography>
              </Box>
              {section.content}
              {index < sections.length - 1 && <Divider sx={{ mt: 4 }} />}
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Divider sx={{ my: 4 }} />
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            This policy is effective as of {effectiveDate}
          </Typography>
        </Box>
      </PrivacyCard>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <ScrollTopSlot>
          <Fab aria-label="Back to top" onClick={scrollToTop}>
            <KeyboardArrowUp />
          </Fab>
        </ScrollTopSlot>
      )}
    </PrivacyContainer>
  );
};
